/**
 * The renderer-agnostic widget seam (WIDGET track, investigation §3 v3).
 *
 * A widget renderer (vanilla widget-dom, future Pixi) consumes `WidgetState`
 * in and `WidgetActions` out through a `StateSource`, and never touches the
 * transport. `createSdkStateSource` (sdk/state-source.ts) is the one data
 * adapter — it folds DealerSDK events for every host (npm consumers and the
 * standalone iframe app alike).
 *
 * Pure TS — no DOM, no framework, no LiveKit imports.
 */

import type { ServiceMode } from '../types/service-mode.js';
import type { TranscriptionSegment } from '../types/transcription.js';
import type { AgentStateType } from '../types/widget-types.js';

import type { MicStatus } from './mic-availability.js';

/**
 * Agent lifecycle as the widget sees it. 'disconnected' means the agent is
 * absent — not yet joined (connecting) or left the room — and renderers
 * disable the PTT until it clears.
 */
export type WidgetAgentState = AgentStateType | 'disconnected';

export interface WidgetState {
  /** Global service mode (normal / paused / voice_offline). */
  mode: ServiceMode;
  /**
   * Recent caption segments, oldest first. Interim updates are folded in place
   * by `segmentId` (see foldCaption), so the last entry is always the live one.
   */
  captions: readonly TranscriptionSegment[];
  agentState: WidgetAgentState;
  micStatus: MicStatus;
  /** Autoplay is blocked — show the "tap to hear your dealer" affordance. */
  audioBlocked: boolean;
  dealerMuted: boolean;
  /** AI suspended after bet-timer expiry, until the next bet confirmation. */
  suspended: boolean;
  /**
   * The mic was granted, but the hold that asked for it had already ended, so
   * the turn it opened was discarded. Prompts the player to hold again instead
   * of leaving the grant looking like nothing happened (#458). Armed only for
   * the session's FIRST successful acquisition (later short taps are not a
   * grant story); cleared by the next startPTT or the dealer's next caption,
   * so it never starves the caption line.
   */
  micJustEnabled: boolean;
  /**
   * True once a PTT acquisition has actually succeeded this session — the
   * single truth for "no permission prompt can interrupt this hold" (drives
   * the prompt-capable-hold gesture rules). Owned by the state source;
   * renderers must not infer it from startPTT resolving, because a dropped
   * re-entrant startPTT resolves without acquiring anything.
   */
  micAcquired: boolean;
}

/** Everything a renderer may ask the service to do. */
export interface WidgetActions {
  startPTT(): Promise<void>;
  endPTT(): Promise<void>;
  cancelPTT(): Promise<void>;
  /** Burn a user gesture to unblock audio playback. */
  startAudio(): Promise<void>;
  toggleMute(): Promise<void>;
  /** Send the player's typed message to the dealer; rejects when not connected. */
  sendText(text: string): Promise<void>;
}

/** The seam between a data adapter and a widget renderer. */
export interface StateSource {
  getState(): WidgetState;
  /** Listener fires on every state change with the new snapshot. Returns unsubscribe. */
  subscribe(listener: (state: WidgetState) => void): () => void;
  readonly actions: WidgetActions;
  /** Detach from the underlying transport; the source goes inert. */
  destroy(): void;
}

/** How many caption segments a WidgetState keeps (renderers show the tail). */
export const CAPTION_HISTORY_LIMIT = 10;

/**
 * Fold a transcription update into the caption list: replace the segment with
 * the same id in place, else append; trim the oldest beyond `limit`.
 */
export function foldCaption(
  captions: readonly TranscriptionSegment[],
  segment: TranscriptionSegment,
  limit: number = CAPTION_HISTORY_LIMIT
): readonly TranscriptionSegment[] {
  const index = captions.findIndex((c) => c.segmentId === segment.segmentId);
  const next =
    index >= 0
      ? [...captions.slice(0, index), segment, ...captions.slice(index + 1)]
      : [...captions, segment];
  return next.length > limit ? next.slice(next.length - limit) : next;
}
